import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import {
  type Perfume,
  AvailablePerfumeIngredients,
  perfumeVolume,
  PageLink,
  adaptPerfumeWorkshopLocation,
  buildIngredients,
  type CategoriesOfIngredients,
  PerfumeTunnelPageStepId,
} from "@/models";
import { debounce } from "@mui/material/utils";
import { showLoader, type LoadingDispatcher } from "@/hooks/Loading";
import { PerfumeConverter } from "@/repositories/PerfumeDb";
import { type PerfumeRecipiesService } from "./PerfumeRecipiesService";
import type { IngredientDialogOpen, VolumeDialogOpen, PerfumeEditor } from "./perfume-editor";
import type { EditPerfumeViewModel } from "./perfume-editor/EditPerfumeViewModel";
import { adaptEditPerfumeViewModel, adaptPerfume } from "./perfume-editor/EditPerfumeViewModel";
import type { IngredientLots } from "@/repositories";
import { beginPendingSave, endPendingSave } from "./app-update";

export function usePerfumeEditor({
  service,
  backendPerfume,
  ingredientLots,
  onEdit,
}: {
  service: PerfumeRecipiesService;
  backendPerfume: Perfume;
  ingredientLots: IngredientLots;
  // called when the client changes the perfume, before the (debounced) save
  onEdit?: () => void;
}): PerfumeEditor {
  const [ingredientDialogOpen, setIngredientDialogOpen] = useState<IngredientDialogOpen>(null);
  const [volumeDialogOpen, setVolumeDialogOpen] = useState<VolumeDialogOpen>(null);

  const [perfumeViewModel, setPerfumeViewModel] = useState<EditPerfumeViewModel>(
    adaptEditPerfumeViewModel(backendPerfume),
  );

  // update viewmodel when backend perfume change. Occurs on
  // - change from outside (another tab or computer)
  // - after saving
  useEffect(() => {
    setPerfumeViewModel((prev: EditPerfumeViewModel) => {
      // no need to update viewmodel there is no change with backendPerfume
      const vmPerfume = adaptPerfume(backendPerfume, prev);
      if (perfumeDeepEqual(vmPerfume, backendPerfume)) {
        return prev;
      }
      return adaptEditPerfumeViewModel(backendPerfume);
    });
  }, [backendPerfume]);

  const [saveError, setSaveError] = useState<boolean>(false);

  const savePerfume = useMemo(() => {
    return debounce((updatedPerfume: Perfume, pendingSaveToken: number) => {
      service.perfumeRepository
        .savePerfume(updatedPerfume)
        .finally(() => endPendingSave(updatedPerfume.recipeId, pendingSaveToken))
        .then(() => setSaveError(false))
        .catch((error) => {
          // typically permission-denied: the perfume is locked or its edit window is over
          console.error(`Can't save perfume ${updatedPerfume.recipeId}`, error);
          setSaveError(true);
        });
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // save perfume when user change it (by calling setPerfumeViewModel)
  useEffect(() => {
    const updatedPerfume = adaptPerfume(backendPerfume, perfumeViewModel);
    if (perfumeDeepEqual(updatedPerfume, backendPerfume)) {
      return;
    }
    onEdit?.();

    // the app must not reload itself (automatic update) before the perfume is saved
    return savePerfume(updatedPerfume, beginPendingSave(updatedPerfume.recipeId));
    // 'backendPerfume' intentionally not included
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfumeViewModel, savePerfume]);

  // save pending changes and lock the perfume in a single write
  const lockPerfume = useCallback(async () => {
    savePerfume.clear();
    endPendingSave(backendPerfume.recipeId);
    await service.perfumeRepository.savePerfume({
      ...adaptPerfume(backendPerfume, perfumeViewModel),
      lockedAt: new Date(),
    });
    forgetActivePerfume(backendPerfume.recipeId);
  }, [savePerfume, service, backendPerfume, perfumeViewModel]);

  const ingredients: CategoriesOfIngredients = useMemo(() => {
    return buildIngredients({
      ingredients: AvailablePerfumeIngredients,
      additionalIngredients: perfumeViewModel.customIngredients,
    });
  }, [perfumeViewModel.customIngredients]);

  return useMemo(
    (): PerfumeEditor => ({
      updatePerfumeViewModel: setPerfumeViewModel,
      ingredientDialogOpen,
      setIngredientDialogOpen,
      volumeDialogOpen,
      setVolumeDialogOpen,
      availableIngredients: ingredients,
      perfumeViewModel,
      totalVolume: perfumeVolume(backendPerfume),
      ingredientLots,
      lockPerfume,
      saveError,
    }),
    [
      lockPerfume,
      saveError,
      setPerfumeViewModel,
      ingredientDialogOpen,
      volumeDialogOpen,
      ingredients,
      perfumeViewModel,
      backendPerfume,
      ingredientLots,
    ],
  );
}

function deepEqual<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function perfumeDeepEqual(l: Perfume, r: Perfume): boolean {
  return (
    l.recipeId === r.recipeId &&
    deepEqual(PerfumeConverter.toFirestore(l), PerfumeConverter.toFirestore(r))
  );
}

// The last perfume created on this device. It is locked when the device creates another one,
// so the next client can't edit it using the browser history.
const localStorageActivePerfumeKey = "active-perfume-id";

function forgetActivePerfume(recipeId: string) {
  if (localStorage.getItem(localStorageActivePerfumeKey) === recipeId) {
    localStorage.removeItem(localStorageActivePerfumeKey);
  }
}

async function lockActivePerfume(service: PerfumeRecipiesService) {
  const activePerfumeId = localStorage.getItem(localStorageActivePerfumeKey);
  if (activePerfumeId === null) {
    return;
  }
  try {
    await service.perfumeRepository.lockPerfume(activePerfumeId);
  } catch (error) {
    // already locked, edit window over or deleted: nothing to protect
    console.log(`Previous perfume ${activePerfumeId} not locked`, error);
  }
  localStorage.removeItem(localStorageActivePerfumeKey);
}

export function createPerfume({
  service,
  loadingDispatcher,
  workshopLocationId,
}: {
  service: PerfumeRecipiesService;
  loadingDispatcher: LoadingDispatcher;
  workshopLocationId: string;
}) {
  console.log(`Creating new perfume for workshop ${workshopLocationId}`);

  const workshop = adaptPerfumeWorkshopLocation(workshopLocationId);
  if (workshop === null) {
    throw Error(`Unknown workshop: ${workshopLocationId}`);
  }

  const create = async () => {
    await lockActivePerfume(service);
    const perfume = await service.perfumeRepository.createPerfume(
      {
        recipeId: uuidv4(),
        formulaNumber: null,
        created: new Date(),
        name: "",
        email: "",
        title: "",
        username: "",
        userLastName: "",
        components: [],
        workshopLocation: workshop,
        comment: "",
        lockedAt: null,
      },
      workshop.formulaNumberPrefix,
    );
    localStorage.setItem(localStorageActivePerfumeKey, perfume.recipeId);
    return perfume;
  };

  return showLoader(loadingDispatcher, create());
}

export function useCreatePerfume({
  service,
  loadingDispatcher,
  workshopLocationId,
  perfumeId,
  canCreatePerfume,
}: {
  service: PerfumeRecipiesService;
  loadingDispatcher: LoadingDispatcher;
  workshopLocationId: string | null;
  perfumeId: string | null;
  // null while the signed in user is not known yet
  canCreatePerfume: boolean | null;
}) {
  const navigate = useNavigate();

  const [cantCreatePerfumeError, setCantCreatePerfumeError] = useState<string>("");

  // a single creation, also in React strict mode (effects run twice, refs are kept)
  const creationStarted = useRef(false);

  useEffect(() => {
    // create new perfume when no recipeId is provided
    if (perfumeId !== null || canCreatePerfume === null || creationStarted.current) {
      return;
    }
    creationStarted.current = true;

    if (workshopLocationId === null) {
      setCantCreatePerfumeError("Please use a QR code to create a perfume");
      return;
    }
    if (!canCreatePerfume) {
      setCantCreatePerfumeError("Only the workshop tablets can create a perfume");
      return;
    }

    (async function () {
      const createdPerfume = await createPerfume({
        service,
        loadingDispatcher,
        workshopLocationId,
      });

      navigate(
        PageLink.perfumeWorkshop({
          perfumeId: createdPerfume.recipeId,
          step: PerfumeTunnelPageStepId.Identification,
        }),
        {
          replace: true,
        },
      );
    })();
  }, [perfumeId, canCreatePerfume, workshopLocationId, service, navigate, loadingDispatcher]);

  return {
    cantCreatePerfumeError,
  };
}

export function useCreateNewPerfume({
  service,
  loadingDispatcher,
  workshopLocationId,
  lockCurrentPerfume,
}: {
  service: PerfumeRecipiesService;
  loadingDispatcher: LoadingDispatcher;
  workshopLocationId?: string;
  // save and lock the perfume being edited, if any
  lockCurrentPerfume?: () => Promise<void>;
}) {
  const navigate = useNavigate();

  const canCreateNewPerfume = workshopLocationId;

  const createNewPerfume = async () => {
    if (canCreateNewPerfume) {
      await lockCurrentPerfume?.();
      const createdPerfume = await createPerfume({
        service,
        loadingDispatcher,
        workshopLocationId,
      });

      // replace: the previous perfume must not stay in the browser history
      navigate(PageLink.perfumeWorkshop({ perfumeId: createdPerfume.recipeId }), {
        replace: true,
      });
    }
  };

  return {
    createNewPerfume,
    canCreateNewPerfume,
  };
}
